import { ReactNode } from 'react'
import { useFormContext } from 'react-hook-form'

import { Employee } from 'modules/core/hooks/useCurrentSession'
import i18n from 'modules/core/lib/i18n'

import { useCostCentersByLegalEntity } from 'modules/cost_centers/hooks/useCostCentersByLegalEntity'
import CostCenterField from 'modules/expenses/components/expenses/Detail/_newForm/Blocks/CostCenters/Field'
import { shouldShowCostCenterBlock } from 'modules/expenses/components/expenses/Detail/_newForm/Blocks/CostCenters/form/schema'
import FormBlock from 'modules/expenses/components/expenses/Detail/_newForm/Sections/FormBlock'
import { CustomizationProps } from 'modules/expenses/components/expenses/Detail/_newForm/withCustomizationFields'
import {
  PermissionsProps,
  WithFieldPermissionsProps,
} from 'modules/expenses/components/expenses/Detail/_newForm/withFieldPermissions'

import { EmptyState } from './EmptyState'

interface Props extends PermissionsProps, CustomizationProps, WithFieldPermissionsProps {
  currentEmployee: Employee
  resourceId?: string
  divider?: boolean
  rightAction?: ReactNode
}

const CostCenterInformation = (props: Props) => {
  const showCostCenterBlock = shouldShowCostCenterBlock(
    props.customizationFields,
    props.resourceType
  )

  const { getValues } = useFormContext()
  const { employeeId, employeeLegalEntity } = getValues()

  const { costCenters, loading, canManageCostCenters } = useCostCentersByLegalEntity({
    legalEntityId: employeeLegalEntity?.id,
    skip: !showCostCenterBlock,
  })

  if (!showCostCenterBlock || loading) return null

  if (costCenters.length === 0) return <EmptyState canManageCostCenters={canManageCostCenters} />

  if (props.isFieldReadOnly && props.expensable?.costCenterDistribution?.length === 0) {
    return null
  }

  return (
    <FormBlock
      title={i18n.t('expenses_v2.forms.new_form.blocks.cost_center_information')}
      subtitle={i18n.t('expenses_v2.forms.new_form.blocks.cost_center_information_description')}
      divider={props.divider}
      rightAction={props.rightAction}
    >
      <CostCenterField
        {...props}
        fieldIdentifier="cost_centers"
        resourceId={props.resourceId}
        employeeId={employeeId}
        isFieldReadOnly={props.isFieldReadOnly}
      />
    </FormBlock>
  )
}

export default CostCenterInformation
